import {
  type StatefulPromiseWithResolvers,
  createStatefulPromiseResolvers,
} from 'src/internal/revamp/fetchUtils/statefulPromise';
import ObservableData from 'src/internal/revamp/observable/ObservableData';

import type {GraphQlDocument} from 'src/internal/revamp/graphql/types';
import type {Subscription} from 'src/internal/revamp/observable/ObservableData';
import type {
  Observer,
  ObserverNextValue,
} from 'src/internal/revamp/observable/types';
import type {PollConfig} from 'src/internal/revamp/polling/types';

export default class ObservableFragment<
  TDocumentNode extends GraphQlDocument<any, any, any, any>,
> extends ObservableData<TDocumentNode> {
  // Promise that is synched with the latest available result returned from
  // the result store.
  private promise: StatefulPromiseWithResolvers<void> | null = null;

  private lastFetchedByRootQueryId: string | null = null;
  private lastFetchSucceeded = false;
  private hasLostAllConsumersSinceFetch = true;
  private suspendedCount = 0;

  private latestRefetchFn: (() => void) | null = null;

  private get hasActiveConsumers(): boolean {
    return this.suspendedCount > 0 || this.observers.size > 0;
  }

  private checkIfLostAllConsumers(): void {
    if (!this.hasActiveConsumers) {
      this.hasLostAllConsumersSinceFetch = true;
    }
  }

  private scheduleConsumerCheck(): void {
    const REACT_COMMIT_GRACE_PERIOD_MS = 150;

    // Delay the check to give React time to render useFragment after Suspense resolves.
    // Without this, we'd check observers.size before useFragment has a chance to call subscribe().
    setTimeout(
      () => this.checkIfLostAllConsumers(),
      REACT_COMMIT_GRACE_PERIOD_MS,
    );
  }

  public setLastFetchedBy(rootQueryId: string, succeeded: boolean): void {
    if (succeeded) {
      this.lastFetchedByRootQueryId = rootQueryId;
      this.lastFetchSucceeded = true;

      if (this.hasActiveConsumers) {
        this.hasLostAllConsumersSinceFetch = false;
      }
    } else {
      this.lastFetchSucceeded = false;
    }
  }

  // Used to determine if the fragment has lost all consumers (in practical terms: whether
  // all useFragment hooks have unmounted) since  the last fetch done by a root query.
  // We use this to determine if we should send the fragment to the network when the root query is executed again.
  public hasConsumerContinuityFor(currentRootQueryId: string): boolean {
    return (
      this.lastFetchedByRootQueryId === currentRootQueryId &&
      this.lastFetchSucceeded &&
      !this.hasLostAllConsumersSinceFetch
    );
  }

  public clearLastFetchedBy(): void {
    this.lastFetchedByRootQueryId = null;
  }

  // Subscribe to future updates. This method does not emit the current value.
  public subscribe(
    observer: Observer<TDocumentNode>,
    pollConfig?: PollConfig,
    refetchFn?: () => void,
  ): Subscription {
    this.observers.add(observer);

    if (refetchFn) {
      this.latestRefetchFn = refetchFn;
    }

    if (pollConfig) {
      this.registerPollConfig(observer, pollConfig);
      // Clean up the suspense observer now that we have a real observer
      this.unregisterSuspensePollConfig();
    }

    if (this.promise?.status === 'pending') {
      this.promise.resolve();
    }

    const watchCleanupFn = this.watch();

    return {
      unsubscribe: (): void => {
        this.observers.delete(observer);
        this.unregisterPollConfig(observer);
        watchCleanupFn();
        this.checkIfLostAllConsumers();
      },
    };
  }

  protected notifyObservers(result: ObserverNextValue<TDocumentNode>): void {
    if (result.errors) {
      this.handleFetchResultForPolling(result.errors);

      const pollingState = this.getPollingState();
      if (pollingState?.isRetrying) {
        // Don't propagate error while retrying - keep previous data visible
        // Don't resolve the promise yet either
        return;
      }
    }

    // Resolve promise if we have data or errors (retry exhausted or no retry)
    if ((result.data || result.errors) && this.promise?.status === 'pending') {
      this.promise.resolve();
    }

    super.notifyObservers(result);
  }

  public startAsyncWaitOnResult(
    triggerRefetch: () => void,
    pollConfig?: PollConfig,
  ): StatefulPromiseWithResolvers<void> {
    this.suspendedCount++;

    this.latestRefetchFn = triggerRefetch;

    // Register poll config for suspense phase. This ensures retry config
    // is available when the fetch completes, even before subscribe is called.
    if (pollConfig) {
      this.registerSuspensePollConfig(pollConfig);
    }

    if (this.promise && this.isCurrentlyFetching()) {
      this.promise.finally(() => {
        this.suspendedCount--;
        this.scheduleConsumerCheck();
      });

      return this.promise;
    }

    this.promise = createStatefulPromiseResolvers<void>();

    const watchCleanupFn = this.watch();

    if (this.lastFetchSucceeded && triggerRefetch) {
      // This means fetch succeeded once but since then data has dissappeared. Trigger a refetch to get data back in cache.
      triggerRefetch();
    }

    this.promise.finally(() => {
      watchCleanupFn();
      this.suspendedCount--;
      this.scheduleConsumerCheck();
    });

    return this.promise;
  }

  protected isCurrentlyFetching(): boolean {
    return this.promise?.status === 'pending';
  }

  protected triggerRefetch(): void {
    if (this.latestRefetchFn) {
      this.latestRefetchFn();
    }
  }

  protected retryFetch(): void {
    this.triggerRefetch();
  }
}
